// src/pages/BinaryClassificationViz.js
import React from "react";
import "./BinaryClassificationViz.css";

// Update these imports to match your actual file paths / names
import cmOneSeason from "./b1.png";   // old model CM
import cmFiveSeasons from "./b1.png"; // new CM (the one you just made)
import accFiveSeasons from "./b2.png"; // Effect of feature engineering
import distFiveSeasons from "./b3.png"; // New xG distribution

const BinaryClassificationViz = () => {
  return (
    <main className="binary-viz">
      {/* Intro */}
      <section className="binary-viz-intro">
        <h1 className="binary-viz-title">
          Binary Classification: Scaling from 1 Season to 5 Seasons
        </h1>
        <p className="binary-viz-subtitle">
          We started with a logistic regression model trained on a single NHL
          season, then expanded to five seasons of on-net shots (2020–2024).
          This page compares how the model&apos;s performance, feature importance,
          and predicted probabilities changed when we added more data and refined
          our feature set.

          Scaling from one season to five seasons tests whether the observed relationships are stable or artifacts of a single year.
        </p>
      </section>

      {/* Data & Features */}
      <section className="binary-viz-section">
        <h2 className="section-title">Data Scale & Feature Set</h2>

        <article className="binary-viz-card">
          <h3 className="card-title">From 1 Season to 5 Seasons</h3>
          <p className="card-text">
            The original model used one season of MoneyPuck data filtered to
            on-net shots. After filtering, we had:
          </p>
          <ul className="card-list">
            <li>
              <strong>1-season model:</strong> 78,891 on-net shots in total,
              with 19,723 shots in the test split.
            </li>
          </ul>
          <p className="card-text">
            For the updated model, we aggregated **five seasons (2020–2024)**:
          </p>
          <ul className="card-list">
            <li>
              Loaded: <strong>564,450</strong> events across 5 seasons
            </li>
            <li>
              Filtered to on-net shots: <strong>396,972</strong> rows
            </li>
          </ul>
          <p className="card-text">
            This much larger dataset reduces the chance of memorizing a single
            season and helps the model learn more stable relationships between
            shot features and goal probability.
          </p>
        </article>

        <article className="binary-viz-card">
          <h3 className="card-title">5-Season Feature Set (19 Features)</h3>
          <p className="card-text">
            The 5-season model uses the following{" "}
            <strong>19 input features</strong>:
          </p>
          <ul className="card-list">
            <li>arenaAdjustedShotDistance</li>
            <li>shotAngleAdjusted</li>
            <li>xCordAdjusted</li>
            <li>yCordAdjusted</li>
            <li>shotType</li>
            <li>shotRebound</li>
            <li>shotRush</li>
            <li>offWing</li>
            <li>
              defendingTeamAverageTimeOnIceOfDefencemenSinceFaceoff
            </li>
            <li>
              defendingTeamAverageTimeOnIceOfForwardsSinceFaceoff
            </li>
            <li>shooterTimeOnIceSinceFaceoff</li>
            <li>averageRestDifference</li>
            <li>period</li>
            <li>isHomeTeam</li>
            <li>timeSinceFaceoff</li>
            <li>timeSinceLastEvent</li>
            <li>distanceFromLastEvent</li>
            <li>theirGoaliePulled</li>
            <li>ourGoaliePulled</li>
          </ul>
          <p className="card-text">
            We still use logistic regression with{" "}
            <code>class_weight='balanced'</code> so the model pays attention to
            the relatively rare goal events instead of always predicting
            &quot;no goal&quot;.
          </p>
        </article>
      </section>

      {/* Performance comparison */}
      <section className="binary-viz-section">
        <h2 className="section-title">Performance: 1 Season vs 5 Seasons</h2>

        <article className="binary-viz-card">
          <h3 className="card-title">Confusion Matrices</h3>
          <p className="card-text">
            Below are the confusion matrices for the original one-season model
            and the new five-season model, both evaluated on held-out test sets
            of on-net shots.
          </p>

          <div className="binary-viz-graph">
            <h4 className="graph-title">1-Season Model</h4>
           
            <pre className="metrics-block small">
{`Test set size: 19,723 on-net shots

TN = 11,750   FP = 5,866
FN =   605   TP = 1,502

Accuracy  = 0.672
Precision = 0.204  (for goals)
Recall    = 0.713  (for goals)`}
            </pre>
          </div>

          <div className="binary-viz-graph">
            <h4 className="graph-title">5-Season Model (2020–2024)</h4>
            <img
              src={cmFiveSeasons}
              alt="Confusion matrix for 5-season model"
              className="graph-image"
            />
            <pre className="metrics-block small">
{`Test set size: 99,243 on-net shots

TN = 59,400   FP = 29,852
FN =  2,849   TP =  7,142

Accuracy  = 0.670
Precision = 0.193  (for goals)
Recall    = 0.715  (for goals)`}
            </pre>
          </div>

          <p className="card-text">
            Accuracy is almost identical (0.672 → 0.670), precision for goals
            remains low, and recall for goals is stable around 0.71. This shows
            that when we scale up to five seasons, the model keeps the same
            behavior: it is tuned to{" "}
            <strong>catch as many real goals as possible</strong> (high recall)
            at the cost of some extra false positives.
          </p>
        </article>

        <article className="binary-viz-card">
          <h3 className="card-title">Effect of Feature Engineering</h3>
          <p className="card-text">
            This plot shows how accuracy changes on the 5-season data as we add
            more engineered features to the model.
          </p>

          <div className="binary-viz-graph">
            <img
              src={accFiveSeasons}
              alt="Effect of feature engineering on accuracy (5-season model)"
              className="graph-image"
            />
          </div>

          <p className="card-text">
            Starting with just a couple of geometric features, accuracy is around
            0.645. As we add more information—shot type, fatigue variables,
            rest difference, and goalie-pulled indicators—accuracy rises to about{" "}
            <strong>0.670</strong> with the full 19-feature model. The gains are
            not huge, but they show that the extra context features contribute
            real signal rather than pure noise.
          </p>
        </article>
      </section>

      {/* Probability distribution */}
      <section className="binary-viz-section">
        <h2 className="section-title">Distribution of Predicted Goal Probabilities</h2>

        <article className="binary-viz-card">
          <h3 className="card-title">xG Distribution for the 5-Season Model</h3>
          <p className="card-text">
            The histogram below shows how the 5-season logistic regression model
            assigns goal probabilities to individual shots.
          </p>

          <div className="binary-viz-graph">
            <img
              src={distFiveSeasons}
              alt="Distribution of predicted goal probabilities for 5-season model"
              className="graph-image"
            />
          </div>

          <p className="card-text">
            Most shots fall in the <strong>0.05–0.7</strong> range, with a smooth
            distribution that reflects a spectrum from low-danger point shots to
            high-danger slot and rush chances. A small bump near 1.0 corresponds
            to extremely dangerous situations (e.g., empty-net chances or shots
            taken very close to the crease).
          </p>
          <p className="card-text">
            Compared to the earlier one-season run, the distribution is smoother
            and less spiky, which is exactly what we expect when we train on more
            data and remove overly &quot;cheaty&quot; features that overfit specific
            situations.
          </p>
        </article>
      </section>

      {/* Feature importance comparison */}
      <section className="binary-viz-section">
        <h2 className="section-title">Feature Importance: 1 Season vs 5 Seasons</h2>

        <article className="binary-viz-card">
          <h3 className="card-title">Top |Weights| – 1-Season Model</h3>
          <p className="card-text">
            The original one-season logistic regression ranked features by
            absolute coefficient value as follows:
          </p>
          <pre className="metrics-block small">
{`arenaAdjustedShotDistance                2.055
xCordAdjusted                              1.014
shotOnEmptyNet                             0.678
shotType_SLAP                              0.243
shotType_SNAP                              0.232
shotAngleAdjusted                          0.134
defendingTeamAverageTimeOnIceOfForwards    0.134
shotType_WRAP                              0.106
averageRestDifference                      0.105
shotType_WRIST                             0.084
shotType_BACK                              0.084
shotType_TIP                               0.075
offWing                                    0.060
yCordAdjusted                              0.054
period                                     0.050
shotRebound                                0.045
isHomeTeam                                 0.028
timeSinceLastEvent                         0.027
shooterTimeOnIceSinceFaceoff               0.024
shotType_DEFL                              0.015`}
          </pre>
          <p className="card-text">
            As expected, shot geometry dominates: distance and lateral
            position (x-coordinate) have by far the largest influence on goal
            odds. Empty-net shots and hard shot types (slap, snap, wrist) also
            show up as strong positive contributors.
          </p>
        </article>

        <article className="binary-viz-card">
          <h3 className="card-title">Top |Weights| – 5-Season Model</h3>
          <p className="card-text">
            With five seasons of data and the updated 19-feature set, the learned
            weights shift slightly but tell a consistent story:
          </p>
          <pre className="metrics-block small">
{`arenaAdjustedShotDistance                1.393
theirGoaliePulled                          0.732
xCordAdjusted                              0.357
shotAngleAdjusted                          0.242
shotType_SLAP                              0.221
defendingTeamAverageTimeOnIceOfForwards    0.193
shotType_SNAP                              0.190
averageRestDifference                      0.147
shotType_BACK                              0.113
shotType_WRAP                              0.091
shooterTimeOnIceSinceFaceoff               0.082
offWing                                    0.046
defendingTeamAverageTimeOnIceOfDefencemen  0.040
shotRebound                                0.040
shotType_WRIST                             0.033
period                                     0.033
timeSinceLastEvent                         0.030
shotType_TIP                               0.026
distanceFromLastEvent                      0.014
timeSinceFaceoff                           0.013`}
          </pre>
          <p className="card-text">
            <strong>Distance, x-coordinate, and angle</strong> remain core drivers
            of goal probability, but with more data, the model also gives a
            strong positive weight to <strong>theirGoaliePulled</strong> (empty
            net at the far end) and continues to highlight{" "}
            <strong>defending forwards&apos; time on ice since faceoff</strong> as an
            important fatigue signal.
          </p>
          <p className="card-text">
            The positive coefficients on{" "}
            <strong>defendingTeamAverageTimeOnIceOfForwardsSinceFaceoff</strong>{" "}
            and <strong>averageRestDifference</strong> support our core theory:
            <strong> longer defensive shifts and worse rest situations increase
            the chance of a goal against</strong>, even after controlling for
            distance and angle.
          </p>
        </article>

        <article className="binary-viz-card">
          <h3 className="card-title">Direction of Effect – 5-Season Model</h3>
          <p className="card-text">
            Looking just at the sign of the coefficients, we can summarize
            factors that increase vs decrease goal odds:
          </p>
          <pre className="metrics-block small">
{`Top 10 increasing goal odds:
theirGoaliePulled                          0.732
shotType_SLAP                              0.221
defendingTeamAverageTimeOnIceOfForwards    0.193
shotType_SNAP                              0.190
averageRestDifference                      0.147
offWing                                    0.046
defendingTeamAverageTimeOnIceOfDefencemen  0.040
shotRebound                                0.040
shotType_WRIST                             0.033
period                                     0.033

Top 10 decreasing goal odds:
arenaAdjustedShotDistance                 -1.393
xCordAdjusted                             -0.357
shotAngleAdjusted                         -0.242
shotType_BACK                             -0.113
shotType_WRAP                             -0.091
shooterTimeOnIceSinceFaceoff             -0.082
timeSinceLastEvent                        -0.030
shotType_TIP                              -0.026
distanceFromLastEvent                     -0.014
timeSinceFaceoff                          -0.013`}
          </pre>
          <p className="card-text">
            Negative coefficients for distance and angle reinforce the basic
            expected-goals intuition: the farther away and the worse the angle,
            the less likely a shot is to go in. Positive coefficients on empty
            nets, hard shot types, and tired defenders capture the situations
            where goals become much more likely.
          </p>
        </article>
      </section>

      {/* Takeaways */}
      <section className="binary-viz-section">
        <h2 className="section-title">Key Takeaways</h2>

        <article className="binary-viz-card">
          <ul className="card-list">
            <li>
              <strong>Scaling to five seasons keeps the model stable:</strong>{" "}
              accuracy and recall are almost unchanged, but now they are backed
              by a much larger sample of shots and goals.
            </li>
            <li>
              <strong>Shot geometry is king:</strong> distance, lateral
              position, and angle dominate both versions of the model.
            </li>
            <li>
              <strong>Fatigue still matters:</strong> defending forwards&apos; time
              on ice and rest difference remain important even with more data,
              supporting our original theory that longer defensive shifts lead to
              more goals against.
            </li>
          
          </ul>
        </article>
      </section>
    </main>
  );
};

export default BinaryClassificationViz;
